import { useEffect, useRef, useState } from 'react';

//hooks
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

//components
import {
  Section,
  Kicker,
  KickerDot,
  SectionTitle,
} from '@/containers-ai/SectionHeading';

//styles
import {
  Theater,
  Tabs,
  Tab,
  TabNumber,
  TabTitle,
  TabSubtitle,
  StageBox,
  StageHead,
  StageHeadTitle,
  StageHeadTag,
  StageStatus,
  StageLog,
  LogRow,
  LogTime,
  LogText,
  StageSummary,
  StageSummaryLabel,
  StageFoot,
} from './ScenarioTheater.styles';
import { Container } from '@/components/Grid';

const LINE_INTERVAL = 950;
const AUTO_ADVANCE_INTERVAL = 9500;

const SCENARIOS = [
  {
    tab: 'Document collection & filing',
    td: 'Docs missing on delivered loads',
    agent: 'Docs Agent',
    nite: '3 delivered loads are missing signed BOLs and a rate con.',
    ref: 'Documents · #LS-48180',
    lines: [
      {
        time: '15:12:04',
        text: '3 delivered loads flagged — documents missing in your TMS.',
      },
      {
        time: '15:12:06',
        text: 'Emailed and called each carrier for the signed BOL and rate con.',
        highlight: true,
      },
      {
        time: '15:12:40',
        text: '2 back — read, validated against the shipment, filed.',
        highlight: true,
      },
      {
        time: '15:13:20',
        text: '1 carrier sent the wrong file — flagged and re-requested.',
        highlight: true,
      },
      {
        time: '15:14:02',
        text: (
          <>
            <b>All 3 filed to your TMS.</b> Every doc matched to its load.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        A stack of delivered loads was missing paperwork. Loadsmart AI chased
        every carrier by email and phone, read and validated each document,
        caught a wrong file, and filed all three to your system of record
        &mdash; <b>your team chased none of them.</b>
      </>
    ),
  },
  {
    tab: 'Tracking & status updates',
    td: "A load's gone quiet",
    agent: 'Tracking Agent',
    nite: "A customer's asking where load #LS-51204 is — and it's gone quiet.",
    ref: 'Tracking · #LS-51204',
    lines: [
      {
        time: '09:14:10',
        text: (
          <>
            No tracking ping on <b>#LS-51204</b> for 4 hours.
          </>
        ),
      },
      {
        time: '09:14:12',
        text: "Reached the carrier — truck's rolling, 40 min behind.",
        highlight: true,
      },
      {
        time: '09:14:40',
        text: 'ETA recalculated and written back to your TMS.',
        highlight: true,
      },
      {
        time: '09:14:42',
        text: 'Still inside the delivery window — nothing needed from you.',
        highlight: true,
      },
      {
        time: '09:14:45',
        text: (
          <>
            <b>Clean status pushed to the customer</b> before they asked again.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        A load stopped reporting and the customer was getting anxious. Loadsmart
        AI chased the carrier, confirmed the truck was moving, updated the ETA,
        and sent the customer a clean status &mdash;{' '}
        <b>the check calls your team never had to make.</b>
      </>
    ),
  },
  {
    tab: 'Tender failures',
    td: 'A tender just fell through',
    agent: 'Procurement Agent',
    nite: 'A carrier just rejected the tender on MEM → ORD, picking up tomorrow.',
    ref: 'Procurement · #LS-49010',
    lines: [
      {
        time: '17:02:01',
        text: 'Tender rejected on MEM → ORD — pickup tomorrow 6am.',
      },
      {
        time: '17:02:03',
        text: 'Re-tendered to the next carrier on your routing guide.',
        highlight: true,
      },
      {
        time: '17:02:44',
        text: 'No response in your 40-min window — moved to the third.',
        highlight: true,
      },
      {
        time: '17:03:10',
        text: 'Covered at contract. Rate con out, dock appointment held.',
        highlight: true,
      },
      {
        time: '17:03:12',
        text: <b>Re-covered before the miss reached your team.</b>,
        highlight: true,
      },
    ],
    summary: (
      <>
        A carrier fell through on a next-day load. Loadsmart AI worked down your
        routing guide, re-tendered until it re-covered at contract, and kept the
        dock appointment &mdash;{' '}
        <b>a rejected tender never became a scramble on your desk.</b>
      </>
    ),
  },
  {
    tab: 'Proactive load audits',
    td: "A load's at risk before pickup",
    agent: 'Audit Agent',
    nite: "Pickups start in 2 hours — and something's off on three loads.",
    ref: 'Audit · AM pickups',
    lines: [
      {
        time: '05:58:00',
        text: 'Pre-pickup audit on 38 loads picking up this morning.',
      },
      {
        time: '05:58:20',
        text: (
          <>
            <b>#LS-50733</b>: tender says reefer, the order needs a dry van.
          </>
        ),
        highlight: true,
      },
      {
        time: '05:58:22',
        text: 'Carrier confirmed the mix-up — corrected before dispatch.',
        highlight: true,
      },
      {
        time: '05:58:40',
        text: '2 more flagged: appointment missing, instructions stale.',
        highlight: true,
      },
      {
        time: '05:59:05',
        text: (
          <>
            <b>All 3 fixed before pickup.</b> No day-of surprises.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        Before the trucks rolled, Loadsmart AI audited the morning&rsquo;s loads
        and caught a wrong equipment type, a missing appointment, and stale
        instructions &mdash; fixing all three ahead of pickup so{' '}
        <b>none turned into detention, a rejection, or a day-of fire drill.</b>
      </>
    ),
  },
  {
    tab: 'Claims processing',
    td: 'Concealed damage on delivery',
    agent: 'Claims Agent',
    nite: 'Receiver just reported concealed damage on delivered load #LS-47788.',
    ref: 'Claims · #LS-47788',
    lines: [
      {
        time: '10:20:00',
        text: (
          <>
            Concealed-damage claim opened on <b>#LS-47788</b>.
          </>
        ),
      },
      {
        time: '10:20:15',
        text: 'Pulled the BOL, POD, photos and delivery notes automatically.',
        highlight: true,
      },
      {
        time: '10:20:40',
        text: 'Validated against carrier liability and the commodity value.',
        highlight: true,
      },
      {
        time: '10:21:10',
        text: 'Drafted the claim packet — ready for your one-click approval.',
        highlight: true,
      },
      {
        time: '10:21:12',
        text: (
          <>
            <b>Filed the moment you approve.</b> Nothing left on the table.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        Concealed damage usually goes unclaimed because filing takes too long.
        Loadsmart AI gathered every document, validated the claim against
        carrier liability, and drafted the packet for your approval &mdash;{' '}
        <b>turning a write-off into recovered dollars.</b>
      </>
    ),
  },
  {
    tab: 'Scheduling & re-scheduling',
    td: 'A pickup just slipped',
    agent: 'Scheduling Agent',
    nite: "The 8am pickup on #LS-52001 just slipped — carrier's running late.",
    ref: 'Scheduling · #LS-52001',
    lines: [
      {
        time: '07:35:00',
        text: 'Carrier flagged the 8am pickup will slip ~3 hours.',
      },
      {
        time: '07:35:20',
        text: 'Checked dock availability — rebooked the slot for 11:15am.',
        highlight: true,
      },
      {
        time: '07:35:40',
        text: 'Delivery appointment shifted to hold the same service window.',
        highlight: true,
      },
      {
        time: '07:35:55',
        text: 'Dock, receiver and customer notified of the new times.',
        highlight: true,
      },
      {
        time: '07:36:00',
        text: (
          <>
            <b>Re-scheduled and everyone&rsquo;s aligned</b> &mdash; no missed
            dock.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        A late carrier threw off a booked pickup. Loadsmart AI found a new dock
        slot, shifted the delivery appointment to protect the service window,
        and notified the dock, receiver and customer &mdash;{' '}
        <b>a slipped pickup didn&rsquo;t cascade into a missed appointment.</b>
      </>
    ),
  },
  {
    tab: 'Detention handling',
    td: 'A truck is about to run out of free time',
    agent: 'Detention Agent',
    nite: 'A truck on #LS-53018 has been waiting 90 minutes for a door.',
    ref: 'Detention · #LS-53018',
    lines: [
      {
        time: '15:30:00',
        text: 'Truck on #LS-53018 has waited 90 min for a door. 30 min of free time left.',
      },
      {
        time: '15:30:04',
        text: 'Dock lead alerted with the truck, the dock and the time left.',
        highlight: true,
      },
      {
        time: '20:02:00',
        text: 'Truck checked out. 6h 02m on site, 4h 02m past free time.',
        highlight: true,
      },
      {
        time: '20:14:30',
        text: 'Carrier’s detention request arrived: 4h 02m at $40/hr.',
        highlight: true,
      },
      {
        time: '20:14:35',
        text: (
          <>
            <b>Matched to the gate record.</b> Approved at $161.33 and logged
            to the facility’s report.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        A truck sat waiting for a door. Loadsmart AI warned the dock lead before
        the free time ran out. When the carrier still billed detention, it
        checked the request against the gate times and the contract rate,
        approved the amount that matched, and added it to the facility’s
        detention report.
      </>
    ),
  },
  {
    tab: 'Purchase order follow-up',
    td: 'Nine POs are due with no appointment',
    agent: 'PO Follow-Up Agent',
    nite: 'Nine POs due this week have no dock appointment.',
    ref: 'PO Follow-Up · Inbound DC-04',
    lines: [
      {
        time: '06:00:00',
        text: 'Checked 412 open POs due in the next 5 days against appointments and bookings.',
      },
      {
        time: '06:00:20',
        text: '9 POs with no appointment. 2 booked loads tracking past their slot.',
        highlight: true,
      },
      {
        time: '06:01:10',
        text: 'Emailed 7 suppliers to release the orders. Rebooked the 2 late loads.',
        highlight: true,
      },
      {
        time: '09:12:00',
        text: '5 suppliers confirmed. Their carriers booked slots for 7 of the 9 POs.',
        highlight: true,
      },
      {
        time: '09:15:00',
        text: (
          <>
            <b>All but 2 POs booked by 9:15.</b> Those 2 are on the buyer’s
            at-risk list.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        Nine purchase orders were due with no truck booked, and two booked loads
        were running late. Loadsmart AI found them before the receiving window,
        got suppliers and carriers to book, rebooked the late loads, and gave
        the buyer a short list of the two still open.
      </>
    ),
  },
  {
    tab: 'Warehouse customer service',
    td: 'A store wants to know where its order is',
    agent: 'Customer Service Agent',
    nite: 'A store just emailed: did PO 4471 arrive, and when does our order ship?',
    ref: 'Customer Service · PO 4471',
    lines: [
      {
        time: '11:02:10',
        text: 'Email from a store manager about inbound PO 4471 and outbound order 88213.',
      },
      {
        time: '11:02:14',
        text: 'Dock schedule: PO 4471 unloaded at Door 6 at 10:40.',
        highlight: true,
      },
      {
        time: '11:02:18',
        text: 'WMS: 46 of 48 cartons received. Order 88213 picked, shipping tomorrow at 9am.',
        highlight: true,
      },
      {
        time: '11:02:25',
        text: 'Replied with the receipt, the ship date and the 2-carton shortage.',
        highlight: true,
      },
      {
        time: '11:02:30',
        text: (
          <>
            <b>Answered in 20 seconds.</b> The shortage is with a rep, receipt
            and BOL attached.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        A store asked two questions that usually take two systems and a few
        minutes to answer. Loadsmart AI checked the dock schedule and the WMS,
        replied with the receipt and the ship date, and passed the carton
        shortage to a rep with everything needed to resolve it.
      </>
    ),
  },
  {
    tab: 'Dock door management',
    td: 'A dock door just went down',
    agent: 'Dock Agent',
    nite: 'Door 4 just went down, with six trucks due before 3pm.',
    ref: 'Dock · DC-02',
    lines: [
      {
        time: '12:40:00',
        text: 'Door 4 marked down until 3pm. 6 appointments affected.',
      },
      {
        time: '12:40:06',
        text: 'Found compatible doors with open capacity for 5 of them.',
        highlight: true,
      },
      {
        time: '12:40:20',
        text: 'Move plan sent to the scheduler, and approved.',
        highlight: true,
      },
      {
        time: '12:41:05',
        text: '5 appointments moved. Each carrier notified of its new door.',
        highlight: true,
      },
      {
        time: '12:41:30',
        text: (
          <>
            <b>No compatible door left for the 6th.</b> Sent to the scheduler
            with 3 open slots tomorrow.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        A dock door went down in the middle of the day. Loadsmart AI found
        compatible doors for five of the six trucks due there, moved them once
        the scheduler approved the plan, told each carrier, and gave the
        scheduler three slots to offer the one it couldn’t place.
      </>
    ),
  },
  {
    tab: 'Yard task orchestration',
    td: 'A reefer trailer arrived after hours',
    agent: 'Yard Agent',
    nite: 'A reefer trailer just checked in at 2am, with no door until 6.',
    ref: 'Yard · Trailer 53-1187',
    lines: [
      {
        time: '02:14:00',
        text: 'Reefer trailer checked in after hours. No door until 6am.',
      },
      {
        time: '02:14:05',
        text: 'Move to a powered reefer spot created and assigned to the night yard driver.',
        highlight: true,
      },
      {
        time: '02:31:40',
        text: 'Move confirmed. Temperature check scheduled for 4am.',
        highlight: true,
      },
      {
        time: '04:06:12',
        text: 'Temperature logged and inside the load’s range.',
        highlight: true,
      },
      {
        time: '05:52:30',
        text: (
          <>
            <b>Trailer spotted at Door 9 for the first shift.</b> Every task
            done in order.
          </>
        ),
        highlight: true,
      },
    ],
    summary: (
      <>
        A temperature-controlled trailer arrived with no door open until
        morning. Loadsmart AI created the tasks it needed in the right order, a
        move to a powered spot, a temperature check and a spot at the door for
        the first shift, and confirmed each one as the yard driver completed
        it.
      </>
    ),
  },
];

export function ScenarioTheater() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleLines, setVisibleLines] = useState(0);
  const [done, setDone] = useState(false);
  const lineTimerRef = useRef(null);
  const autoAdvanceRef = useRef(null);
  const userInteractedRef = useRef(false);

  const active = SCENARIOS[activeIndex];

  function selectScenario(index) {
    userInteractedRef.current = true;
    if (autoAdvanceRef.current) {
      clearInterval(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    setActiveIndex(index);
  }

  useEffect(() => {
    if (prefersReducedMotion || userInteractedRef.current) return undefined;

    autoAdvanceRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % SCENARIOS.length);
    }, AUTO_ADVANCE_INTERVAL);

    return () => clearInterval(autoAdvanceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (lineTimerRef.current) clearInterval(lineTimerRef.current);
    setDone(false);

    if (prefersReducedMotion) {
      setVisibleLines(active.lines.length);
      setDone(true);
      return undefined;
    }

    setVisibleLines(0);
    let li = 0;
    lineTimerRef.current = setInterval(() => {
      if (li < active.lines.length) {
        li += 1;
        setVisibleLines(li);
      } else {
        clearInterval(lineTimerRef.current);
        lineTimerRef.current = null;
        setDone(true);
      }
    }, LINE_INTERVAL);

    return () => clearInterval(lineTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, prefersReducedMotion]);

  return (
    <Section id="scenario-theater">
      <Container>
        <Kicker>
          <KickerDot />
          Watch it run your freight
        </Kicker>
        <SectionTitle>Pick the fire. Watch it go out.</SectionTitle>

        <Theater>
          <Tabs role="tablist">
            {SCENARIOS.map((scenario, index) => (
              <Tab
                key={scenario.tab}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                $active={index === activeIndex}
                onClick={() => selectScenario(index)}
              >
                <TabNumber $active={index === activeIndex}>
                  {String(index + 1).padStart(2, '0')}
                </TabNumber>
                <div>
                  <TabTitle $active={index === activeIndex}>
                    {scenario.tab}
                  </TabTitle>
                  <TabSubtitle>{scenario.td}</TabSubtitle>
                </div>
              </Tab>
            ))}
          </Tabs>

          <StageBox>
            <StageHead>
              <div>
                <StageHeadTitle>{active.nite}</StageHeadTitle>
                <StageHeadTag>{active.ref}</StageHeadTag>
              </div>
              <StageStatus $done={done}>
                {done ? 'Resolved' : 'Working'}
              </StageStatus>
            </StageHead>
            <StageLog>
              {active.lines.map((line, index) => (
                <LogRow
                  key={index}
                  $highlight={line.highlight}
                  $show={index < visibleLines}
                >
                  <LogTime>{line.time}</LogTime>
                  <LogText $highlight={line.highlight}>{line.text}</LogText>
                </LogRow>
              ))}
            </StageLog>
            <StageSummary $show={done}>
              <StageSummaryLabel>What just happened</StageSummaryLabel>
              {active.summary}
            </StageSummary>
            <StageFoot>
              <span>Loadsmart AI · scripted session</span>
              <span>{active.agent}</span>
            </StageFoot>
          </StageBox>
        </Theater>
      </Container>
    </Section>
  );
}
